import type { DeploymentBadgeProps } from "./types";
import { defineComponent } from "vue";

import Badge from "@/components/ui/badge/Badge";
import Icon from "@/components/ui/icon/Icon";
import { hasSubscriptionFeature } from "@/stores/user-profile.store";

export default defineComponent<DeploymentBadgeProps>({
  name: "DeploymentBadge",
  props: {
    agent: {
      type: Object,
      required: true,
    },
    deployment: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    // Truncate long values (especially for SIP URIs)
    function getTruncatedValue(value: string, maxLength: number = 20): string {
      if (value.length <= maxLength) return value;
      return `${value.substring(0, maxLength)}...`;
    }

    return () => (
      <div class="cursor-default rounded-md transition-all duration-200 hover:scale-105">
        <Badge
          color={props.deployment.disabled ? "gray" : props.deployment.color}
          size="sm"
          bordered
          shadow
          class={`flex max-w-40 items-center ${props.deployment.disabled ? "opacity-60" : ""} `}
        >
          {props.deployment.type === "sip" ? (
            // SIP: Ícone SIP + indicador direcional
            <>
              <Icon icon="lucide:phone" class="h-3.5 w-3.5 shrink-0" />
              {(() => {
                const hasInbound = props.deployment.data.enable_inbound;
                const hasOutbound = props.deployment.data.enable_outbound && hasSubscriptionFeature("sip_outbound");

                if (hasInbound && hasOutbound) {
                  // Ambos ativos: setas bidirecionais
                  return <Icon icon="lucide:arrow-up-down" class="h-3.5 w-3.5 shrink-0" />;
                } else if (hasInbound) {
                  // Apenas inbound: seta para baixo
                  return <Icon icon="lucide:arrow-down" class="h-3.5 w-3.5 shrink-0" />;
                } else if (hasOutbound) {
                  // Apenas outbound: seta para cima
                  return <Icon icon="lucide:arrow-up" class="h-3.5 w-3.5 shrink-0" />;
                }
                // Nenhum ativo: sem indicador adicional
                return null;
              })()}
            </>
          ) : (
            // Outros deployments: ícone único
            <Icon icon={props.deployment.icon} class="h-3.5 w-3.5 shrink-0" />
          )}
          <span class="ml-1 truncate text-xs font-medium">
            {getTruncatedValue(props.deployment.value)}
          </span>
          {props.deployment.disabled && (
            <Icon
              icon="lucide:alert-triangle"
              class="h-3 w-3 shrink-0 text-red-9"
            />
          )}
        </Badge>
      </div>
    );
  },
});
